import { useState, useMemo } from "react";
import { Card, Input, Select, Button, Badge, Table, Typography, Row, Col, Tooltip, } from "antd";
import { SearchOutlined, UserOutlined, RiseOutlined, AimOutlined, CheckCircleOutlined } from "@ant-design/icons";
import ExportarCSV from "./ExportarCSV";
const { Option } = Select;
const { Title } = Typography;

const parsePeriodoToDate = (periodo) => {
    const clean = periodo
        .toLowerCase()
        .replace(/-/g, " ")
        .replace(/\s+/g, " ")
        .trim(); // Normaliza: guiones, espacios, etc.

    const match = clean.match(/(\w+)\s*(\w+)?\s*(\d{4})$/); // captura mes1 mes2 año

    if (match) {
        const [, mes1, , año] = match;
        const meses = {
            enero: 0,
            febrero: 1,
            marzo: 2,
            abril: 3,
            mayo: 4,
            junio: 5,
            julio: 6,
            agosto: 7,
            septiembre: 8,
            octubre: 9,
            noviembre: 10,
            diciembre: 11,
        };
        return new Date(parseInt(año), meses[mes1.toLowerCase()] || 0, 1);
    }
    return new Date(0); // si no se puede parsear, lo manda al inicio
};


const DashboardPrincipal = ({ data, onNavigateToART, onNavigateToDistribucion }) => {
    const [busqueda, setBusqueda] = useState("");
    const [filtroMunicipio, setFiltroMunicipio] = useState("todos");
    const [filtroDepto, setFiltroDepto] = useState("todos");
    const [filtroProyecto, setFiltroProyecto] = useState("todos");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const periodosOrdenados = useMemo(() => {
        const periodosSet = new Set();
        data.participantes.forEach((p) => {
            if (p.resultados) {
                Object.values(p.resultados).forEach((proyecto) => {
                    if (proyecto.subcarpetas) {
                        Object.keys(proyecto.subcarpetas).forEach((periodo) => {
                            const limpio = periodo
                                .replace(/^\d+\.\s*Visitas\s*/i, "")
                                .trim();
                            periodosSet.add(limpio);
                        });
                    }
                });
            }
        });
        return Array.from(periodosSet).sort((a, b) => parsePeriodoToDate(a) - parsePeriodoToDate(b));
    }, [data.participantes]);

    const participantesFiltrados = useMemo(() => {
        return data.participantes.filter((p) => {
            const byBusqueda =
                !busqueda ||
                p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
                p.cedula.includes(busqueda);
            const byMunicipio = filtroMunicipio === "todos" || p.municipio === filtroMunicipio;
            const byDepto = filtroDepto === "todos" || p.departamento === filtroDepto;
            const byProyecto = filtroProyecto === "todos" || p.proyectos === filtroProyecto;
            return byBusqueda && byMunicipio && byDepto && byProyecto;
        });
    }, [data.participantes, busqueda, filtroMunicipio, filtroDepto, filtroProyecto]);

    const metricas = useMemo(() => {
        const total = participantesFiltrados.length;
        const conArchivos = participantesFiltrados.filter((p) => p.tiene_archivos).length;
        const totalRegistros = participantesFiltrados.reduce((sum, p) => {
            return (
                sum +
                Object.values(p.resultados || {}).reduce(
                    (ps, proyecto) => ps + (proyecto.total || 0),
                    0
                )
            );
        }, 0);

        // Calculamos metaGeneral dependiendo del filtro
        let metaGeneral = 0;

        if (busqueda.trim()) {
            if (total === 1) {
                metaGeneral = Math.round(Number(participantesFiltrados[0].meta_x_participante) || 0);
            } else {
                metaGeneral = Math.round(
                    participantesFiltrados.reduce(
                        (acc, p) => acc + (Number(p.meta_x_participante) || 0),
                        0
                    )
                );
            }
        } else {
            const metas = new Set();
            participantesFiltrados.forEach((p) => metas.add(Number(p.meta_x_proyecto) || 0));
            metaGeneral = Math.round([...metas].reduce((acc, m) => acc + m, 0));
        }
        const archivos = totalRegistros;

        const porcentajeAvance = metaGeneral > 0
            ? Math.round((archivos / metaGeneral) * 100)
            : 0;

        return {
            total,
            conArchivos,
            totalRegistros,
            metaGeneral,
            archivosEjecutados: archivos,
            porcentajeAvance,
        };
    }, [participantesFiltrados, busqueda]);


    const getArchivosPorPeriodo = (p, periodo) => {
        if (!p.resultados) return 0;

        for (const proyecto of Object.values(p.resultados)) {
            const subcarpetas = proyecto.subcarpetas || {};
            for (const [key, val] of Object.entries(subcarpetas)) {
                const keyLimpio = key.replace(/^\d+\.\s*Visitas\s*/i, "");
                if (keyLimpio === periodo) {
                    return typeof val === "number" ? val : 0;
                }
            }
        }
        return 0;
    };

    const columns = [
        {
            title: "#",
            fixed: "left",
            width: 60,
            render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        },
        {
            title: "Nombre Participante",
            dataIndex: "nombre",
            fixed: "left",
            width: 200,
            render: (text, record) => (
                <span>
                    {text} {!record.tiene_archivos}
                </span>
            ),
        },
        { title: "Cédula", dataIndex: "cedula", width: 120 },
        { title: "Departamento", dataIndex: "departamento", width: 150 },
        { title: "Municipio", dataIndex: "municipio", width: 150 },
        { title: "Proyecto", dataIndex: "proyectos", width: 250 },
        ...periodosOrdenados.map((periodo) => ({
            title: (
                <div style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{periodo}</div>
            ),
            width: 200,
            dataIndex: periodo,
            align: "center",
            render: (_, record) => {
                const cant = getArchivosPorPeriodo(record, periodo);
                return (
                    <Tooltip title={`Archivos en ${periodo}`}>
                        <Badge
                            count={cant}
                            showZero
                            style={{ backgroundColor: cant > 0 ? "#1890ff" : "#d9d9d9" }}
                        />
                    </Tooltip>
                );
            },
        })),
        {
            title: "Total Archivos",
            width: 150,
            align: "center",
            render: (_, record) => {
                const total = Object.values(record.resultados || {}).reduce(
                    (sum, proyecto) => sum + (proyecto.total || 0),
                    0
                );
                return (
                    <Badge
                        count={total}
                        showZero
                        style={{ backgroundColor: total ? "#52c41a" : "#d9d9d9" }}
                    />
                );
            },
        },
    ];
  const filtrosActivos = {
        busqueda: busqueda || null,
        municipio: filtroMunicipio !== "todos" ? filtroMunicipio : null,
        departamento: filtroDepto !== "todos" ? filtroDepto : null,
        proyecto: filtroProyecto !== "todos" ? filtroProyecto : null
    };
    return (
        <div style={{ padding: 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={8} lg={5}>
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Buscar por nombre o cédula"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                    <Select
                        value={filtroMunicipio}
                        onChange={setFiltroMunicipio}
                        style={{ width: "100%" }}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                    >
                        <Option value="todos">Todos los municipios</Option>
                        {data.municipios?.map((m) => <Option key={m}>{m}</Option>)}
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                    <Select
                        value={filtroDepto}
                        onChange={setFiltroDepto}
                        style={{ width: "100%" }}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                    >
                        <Option value="todos">Todos los departamentos</Option>
                        {data.departamentos?.map((d) => <Option key={d}>{d}</Option>)}
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                    <Select
                        value={filtroProyecto}
                        onChange={setFiltroProyecto}
                        style={{ width: "100%" }}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                    >
                        <Option value="todos">Todos los proyectos</Option>
                        {data.proyectosUnicos?.map((p) => <Option key={p}>{p}</Option>)}
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={4}>
                    <Button
                        block
                        onClick={() => {
                            setBusqueda("");
                            setFiltroMunicipio("todos");
                            setFiltroDepto("todos");
                            setFiltroProyecto("todos");
                        }}
                        icon={<SearchOutlined rotate={90} />}
                    >
                        Limpiar
                    </Button>

                </Col>
                <Col>
                            <ExportarCSV
                                data={data}
                                participantesFiltrados={participantesFiltrados}
                                periodosOrdenados={periodosOrdenados}
                                metricas={metricas}
                                filtrosActivos={filtrosActivos}
                            />
                        </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Card  hoverable style={{ height: 120, padding: 10 }}>
                        <div style={{ textAlign: "center" }}>
                            <UserOutlined style={{ fontSize: 24, color: "#3b82f6" }} />
                            <Title level={4} style={{ marginTop: 10 }}>Total Participantes</Title>
                            <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.total}</p>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Card  hoverable style={{ height: 120, padding: 10 }}>
                        <div style={{ textAlign: "center" }}>
                            <AimOutlined style={{ fontSize: 24, color: "#3b82f6" }} />
                            <Title level={4} style={{ marginTop: 10 }}>Visitas Proyectadas</Title>
                            <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.metaGeneral}</p>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Card  hoverable style={{ height: 120, padding: 10 }}>
                        <div style={{ textAlign: "center" }}>
                            <CheckCircleOutlined style={{ fontSize: 24, color: "#10b981" }} />
                            <Title level={4} style={{ marginTop: 10 }}>Visitas Realizadas</Title>
                            <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.archivosEjecutados}</p>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                    <Card  hoverable style={{ height: 120, padding: 10 }}>
                        <div style={{ textAlign: "center" }}>
                            <RiseOutlined style={{ fontSize: 24, color: "#f59e0b" }} />
                            <Title level={4} style={{ marginTop: 10 }}>% de Avance</Title>
                            <p style={{ fontSize: 18, fontWeight: 500 }}>{metricas.porcentajeAvance}%</p>
                        </div>
                    </Card>
                </Col>
            </Row>
            <Table
                bordered={true}
                size="small"
                rowKey="oid"
                columns={columns}
                dataSource={participantesFiltrados}
                scroll={{
                    x: 1500,
                    y: 500, 
                }}
                pagination={{
                    current: currentPage,
                    pageSize,
                    pageSizeOptions: ["10", "20", "30", "50", "100"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    locale: {
                        jump_to: "",
                        page: "",
                    },
                    onChange: (page, newPageSize) => {
                        setCurrentPage(page);
                        if (newPageSize !== pageSize) {
                            setPageSize(newPageSize);
                        }
                    },
                    onShowSizeChange: (_, newSize) => {
                        setPageSize(newSize);
                        setCurrentPage(1);
                    },
                }}
                style={{ overflowX: "auto" }}
            />
            {participantesFiltrados.length === 0 && (
                <div style={{ textAlign: "center", padding: 20 }}>No se encontraron participantes.</div>
            )}
        </div>
    );
};

export default DashboardPrincipal;